import { cva, type VariantProps } from "class-variance-authority";
import type { PolymorphicProps } from "@/lib/types";
import { cls } from "@/lib/utils";
import type { RequireAriaLabel } from "@/types/aria";

export type ButtonElement = "button";
export type ButtonProps<E extends React.ElementType = ButtonElement> =
  PolymorphicProps<E, RequireAriaLabel & VariantProps<typeof buttonVariants>>;

export const buttonVariants = cva(
  cls`
    inline-flex items-center justify-center
    rounded-sm
    text-sm font-medium font-button
    ring-offset-background
    transition-colors
    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
    disabled:pointer-events-none disabled:opacity-50
  `,
  {
    defaultVariants: {
      size: "default",
      variant: "default",
    },
    variants: {
      size: {
        default: "h-10 px-4 py-2",
        icon: "h-10 w-10 rounded-sm",
        lg: "h-11 rounded-sm px-8",
        sm: "h-9 rounded-sm px-3",
        xs: "h-7 rounded-sm px-1 text-xs",
      },
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/80 shadow-lg",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/50 shadow-lg",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "underline-offset-4 hover:underline",
        outline: cls`
          border-2 border-input
          text-outline-foreground
          bg-transparent
          hover:bg-accent hover:text-accent-foreground
        `,
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-lg",
        tertiary:
          "bg-tertiary text-tertiary-foreground hover:bg-tertiary/80 shadow-lg",
      },
    },
  },
);
